/**
 * @file useScrollCollapse.ts
 * @brief Hide chrome on scroll-down, reveal on scroll-up (all viewports).
 *
 * @details
 * Mirrors the mobile app pattern (`useCollapsibleFooter`):
 * scroll down past a threshold → collapse; scroll up → expand.
 * At scroll top the chrome is always visible.
 *
 * Uses a directional run + threshold (not raw frame-to-frame deltas) so
 * touch micro-jitter on small screens cannot flip-flop the chrome. A short
 * cooldown after each toggle ignores residual scroll events from layout /
 * momentum so header/footer cannot get stuck hidden until a refresh.
 *
 * Related: layouts/components/AdminShellLayout.tsx (consumes `collapsed`),
 *          FRONTEND/src/shared/hooks/useCollapsibleFooter.ts (source pattern).
 *
 * @author DES
 * @date 19-06-2026
 */

import { useEffect, useRef, useState, type RefObject } from "react";

/** Px of directional scroll required before chrome hides/shows. */
const SCROLL_THRESHOLD = 14;
/** Keep chrome pinned while near the top of the scroller. */
const TOP_REST_ZONE = 24;
/** Ignore scroll events this long after a toggle (matches CSS 300ms ease). */
const TOGGLE_COOLDOWN_MS = 320;

export function useScrollCollapse(
  scrollRef: RefObject<HTMLElement | null>,
  /**
   * When this returns `true`, scroll events are ignored so the collapse
   * state can't flip mid-flight — used for programmatic anchor scrolls.
   */
  isPaused?: () => boolean,
) {
  const [collapsed, setCollapsed] = useState(false);
  const lastY = useRef(0);
  const anchorY = useRef(0);
  const lastDir = useRef<"up" | "down" | null>(null);
  const hidden = useRef(false);
  const cooldownUntil = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const expand = () => {
      if (!hidden.current) return;
      hidden.current = false;
      cooldownUntil.current = Date.now() + TOGGLE_COOLDOWN_MS;
      setCollapsed(false);
    };

    const collapse = () => {
      if (hidden.current) return;
      hidden.current = true;
      cooldownUntil.current = Date.now() + TOGGLE_COOLDOWN_MS;
      setCollapsed(true);
    };

    const onScroll = () => {
      if (isPaused?.()) return;
      if (Date.now() < cooldownUntil.current) {
        lastY.current = el.scrollTop;
        return;
      }

      const y = el.scrollTop;
      const delta = y - lastY.current;
      lastY.current = y;

      // Near the top: always restore chrome.
      if (y <= TOP_REST_ZONE) {
        anchorY.current = y;
        lastDir.current = null;
        expand();
        return;
      }

      // Ignore sub-pixel noise / equal frames.
      if (Math.abs(delta) < 0.5) return;

      const dir: "up" | "down" = delta > 0 ? "down" : "up";
      if (dir !== lastDir.current) {
        lastDir.current = dir;
        anchorY.current = y;
      }

      const runDistance = y - anchorY.current;
      if (runDistance > SCROLL_THRESHOLD) {
        collapse();
      } else if (runDistance < -SCROLL_THRESHOLD) {
        expand();
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, isPaused]);

  return {
    collapsed,
    expand: () => {
      hidden.current = false;
      cooldownUntil.current = Date.now() + TOGGLE_COOLDOWN_MS;
      setCollapsed(false);
    },
  };
}
